import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { adminChangePassword, adminGetDiscount, adminSetDiscount } from "../../api";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, Eye, EyeOff, Tag } from "lucide-react";

const rules = [
  { key: "len", label: "At least 10 characters" },
  { key: "digit", label: "Contains at least one number" },
];

const check = (pw) => ({
  len: (pw || "").length >= 10,
  digit: /\d/.test(pw || ""),
});

const AdminSettings = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  // Site-wide sale
  const [discount, setDiscount] = useState({ active: false, percent: 10, message: "Festive Sale — 10% off sitewide" });
  const [discountLoading, setDiscountLoading] = useState(true);
  const [discountSaving, setDiscountSaving] = useState(false);

  useEffect(() => {
    adminGetDiscount()
      .then((d) => setDiscount({ active: !!d.active, percent: d.percent, message: d.message || "" }))
      .catch(() => toast.error("Could not load sale settings."))
      .finally(() => setDiscountLoading(false));
  }, []);

  const saveDiscount = async (e) => {
    e.preventDefault();
    const pct = Number(discount.percent);
    if (!pct || pct < 1 || pct > 90) return toast.error("Discount must be between 1% and 90%.");
    setDiscountSaving(true);
    try {
      const saved = await adminSetDiscount({ active: discount.active, percent: pct, message: discount.message });
      setDiscount(saved);
      toast.success(saved.active ? `Sale is live — ${Math.round(saved.percent)}% off everything.` : "Sale turned off.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save sale settings.");
    } finally {
      setDiscountSaving(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const tog = (k) => () => setShow({ ...show, [k]: !show[k] });
  const passes = check(form.next);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current) return toast.error("Enter your current password.");
    if (!passes.len || !passes.digit) return toast.error("New password doesn't meet the rules.");
    if (form.next !== form.confirm) return toast.error("New passwords don't match.");
    if (form.next === form.current) return toast.error("New password must be different from current.");

    setSaving(true);
    try {
      await adminChangePassword({ current_password: form.current, new_password: form.next });
      toast.success("Password updated. Please use your new password next time you sign in.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "h-12 rounded-none bg-white border-[var(--sand)] focus-visible:ring-[var(--copper)]";

  return (
    <div className="p-8 lg:p-12 max-w-3xl">
      <p className="text-xs tracking-[0.3em] uppercase text-[var(--copper)] mb-3">— Studio Settings</p>
      <h1 className="font-serif-display text-4xl text-[var(--espresso)]">Account</h1>
      <p className="text-sm text-[var(--espresso)]/60 mt-2">Signed in as <span className="text-[var(--espresso)]">{user?.email}</span></p>

      {/* Site-wide sale */}
      <form onSubmit={saveDiscount} data-testid="admin-discount-form" className="mt-10 bg-white border border-[var(--sand)] p-8 space-y-6">
        <div className="flex items-center justify-between pb-4 border-b border-[var(--sand)]">
          <div className="flex items-center gap-3">
            <Tag className="w-4 h-4 text-[var(--copper)]" />
            <h2 className="text-xs tracking-[0.2em] uppercase text-[var(--espresso)]">Site-wide sale</h2>
          </div>
          {discountLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-[var(--espresso)]/40" />
          ) : (
            <label className="flex items-center gap-3 cursor-pointer" data-testid="discount-active-label">
              <span className={`text-[10px] tracking-[0.2em] uppercase ${discount.active ? "text-[var(--copper)]" : "text-[var(--espresso)]/50"}`}>
                {discount.active ? "Live" : "Off"}
              </span>
              <Switch
                checked={discount.active}
                onCheckedChange={(v) => setDiscount({ ...discount, active: v })}
                data-testid="discount-active-toggle"
              />
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--espresso)]/60">Discount % (1–90)</label>
            <Input
              type="number" min="1" max="90" step="1"
              value={discount.percent}
              onChange={(e) => setDiscount({ ...discount, percent: e.target.value })}
              data-testid="discount-percent-input"
              className={inputCls + " mt-2"}
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--espresso)]/60">Banner message</label>
            <Input
              value={discount.message}
              onChange={(e) => setDiscount({ ...discount, message: e.target.value })}
              data-testid="discount-message-input"
              maxLength={120}
              placeholder="Festive Sale — 10% off sitewide"
              className={inputCls + " mt-2"}
            />
          </div>
        </div>

        {discount.active && (
          <p className="text-xs text-[var(--copper)] bg-[var(--copper)]/5 border border-[var(--copper)]/20 px-4 py-3" data-testid="discount-live-note">
            Live now: every product shows a strike-through price and customers pay {Math.round(Number(discount.percent) || 0)}% less automatically at checkout. Custom commissions are quoted separately and are not affected.
          </p>
        )}

        <button
          type="submit"
          disabled={discountSaving || discountLoading}
          data-testid="discount-save-btn"
          className="h-12 px-8 vc-btn-copper text-xs tracking-[0.2em] uppercase disabled:opacity-60 flex items-center gap-2"
        >
          {discountSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : "Save sale settings"}
        </button>
      </form>

      <form onSubmit={submit} data-testid="admin-password-form" className="mt-10 bg-white border border-[var(--sand)] p-8 space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-[var(--sand)]">
          <KeyRound className="w-4 h-4 text-[var(--copper)]" />
          <h2 className="text-xs tracking-[0.2em] uppercase text-[var(--espresso)]">Change password</h2>
        </div>

        {[
          { key: "current", label: "Current password", testid: "current-password" },
          { key: "next", label: "New password", testid: "new-password" },
          { key: "confirm", label: "Confirm new password", testid: "confirm-password" },
        ].map((f) => (
          <div key={f.key}>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--espresso)]/60">{f.label}</label>
            <div className="relative mt-2">
              <Input
                type={show[f.key] ? "text" : "password"}
                value={form[f.key]}
                onChange={upd(f.key)}
                data-testid={`admin-${f.testid}-input`}
                className={inputCls}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={tog(f.key)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--espresso)]/50 hover:text-[var(--copper)]"
                tabIndex={-1}
                aria-label={show[f.key] ? "Hide password" : "Show password"}
              >
                {show[f.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}

        <ul className="space-y-1.5 text-xs">
          {rules.map((r) => {
            const ok = passes[r.key];
            return (
              <li key={r.key} className={`flex items-center gap-2 ${ok ? "text-[var(--copper)]" : "text-[var(--espresso)]/50"}`}>
                <ShieldCheck className={`w-3.5 h-3.5 ${ok ? "opacity-100" : "opacity-30"}`} />
                {r.label}
              </li>
            );
          })}
        </ul>

        <button
          type="submit"
          disabled={saving}
          data-testid="admin-save-password-btn"
          className="h-12 px-8 vc-btn-copper text-xs tracking-[0.2em] uppercase disabled:opacity-60 flex items-center gap-2"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : "Save new password"}
        </button>
      </form>

      <p className="text-xs text-[var(--espresso)]/50 mt-6">
        Tip: Use a password manager. If you forget the new password, we'll need to reset it directly in the database.
      </p>
    </div>
  );
};

export default AdminSettings;
